import { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { LockerThingTypeMrn } from "shared/lockerData"
import type { Locker } from "shared/locker"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { useAppSelector } from "../../app/hooks"
import { ModeContext } from "../../ModeContext"
import type { Sl2Mode } from "../../ModeProvider"
import { selectCurrentTenant, selectMe, selectTenantId } from "../me/meSlice"
import { useGetThingsQuery } from "../things/thingsApi"
import { selectThingsByThingTypeMrn } from "../things/thingsSlice"
import useFeatures from "../things/useFeatures"
import {
  type Sl2MessageContext,
  buildSl2MessageContext,
} from "./sl2MessageContext"
import { selectInAppSnapshot, snapshotUsesUiSurfaces } from "./inAppMessagesSlice"

/** Sorted, unique `data-ui` ids in the document. */
export function readUiSurfaces(root: ParentNode = document): string[] {
  const ids = new Set<string>()
  root.querySelectorAll("[data-ui]").forEach((el) => {
    const id = el.getAttribute("data-ui")
    if (id) ids.add(id)
  })
  return Array.from(ids).sort()
}

const SURFACES_DEBOUNCE_MS = 200

/**
 * `page.uiSurfaces`, tracked only while a live message needs it (an inline anchor or a
 * condition on it). A debounced MutationObserver picks up anchors that render after data loads.
 */
function useUiSurfaces(enabled: boolean, pathname: string) {
  const [surfaces, setSurfaces] = useState<string[] | undefined>(undefined)
  useEffect(() => {
    if (!enabled) {
      setSurfaces(undefined)
      return
    }
    const update = () =>
      setSurfaces((prev) => {
        const next = readUiSurfaces()
        return prev && prev.join("|") === next.join("|") ? prev : next
      })
    update()
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new MutationObserver(() => {
      clearTimeout(timer)
      timer = setTimeout(update, SURFACES_DEBOUNCE_MS)
    })
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-ui"],
    })
    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [enabled, pathname])
  return surfaces
}

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth)
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])
  return width
}

/**
 * The SL2 audience context (spec §Attribute catalog, SL2 table) from state the console
 * already holds. The `GetLockers` query is the one `OsLayout` issues, so it shares that cache
 * entry and adds no request; it only tells a loaded empty list from a list not loaded yet.
 */
export function useMessageContext(): Sl2MessageContext {
  const { pathname } = useLocation()
  const { i18n } = useTranslation()
  const mode = (useContext(ModeContext) || "standalone") as Sl2Mode
  const { user, tu } = useAppSelector(selectMe)
  const tenantId = useAppSelector(selectTenantId)
  const tenant = useAppSelector(selectCurrentTenant)
  const lockers = useAppSelector((s) =>
    selectThingsByThingTypeMrn(s, LockerThingTypeMrn),
  ) as Locker[]
  const { isSuccess: lockersLoaded } = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetLockers,
        "ap.tenantId": tenantId,
        tenantId,
      },
    },
    { skip: !tenantId },
  )
  const { activatedFeatures } = useFeatures(tenantId)
  const snapshot = useAppSelector(selectInAppSnapshot)
  const uiSurfaces = useUiSurfaces(snapshotUsesUiSurfaces(snapshot), pathname)
  const width = useWindowWidth()

  return useMemo(
    () =>
      buildSl2MessageContext({
        tenantId,
        tenant,
        user,
        tu,
        activatedFeatures,
        lockers: lockersLoaded ? lockers : undefined,
        language: i18n.language ?? "fr",
        mode,
        pathname,
        uiSurfaces,
        width,
      }),
    [
      tenantId,
      tenant,
      user,
      tu,
      activatedFeatures,
      lockersLoaded,
      lockers,
      i18n.language,
      mode,
      pathname,
      uiSurfaces,
      width,
    ],
  )
}
